/**
 * Canonical portal-activation-status vocabulary (#1548).
 *
 * Single source of truth for "has this person activated their platform
 * login" across every surface that lists people — project team, contacts,
 * settings team, resources, assignee pickers. Before this module three
 * surfaces each reimplemented the same active/invited/not_invited badge
 * (ContactsPage.PortalBadge, ProjectDetailPageNew.PortalStatusBadge,
 * ContactProfilePage's inline Badge variant map) plus SettingsTeamPage had
 * two more ad hoc inline pills keyed off invite_pending/invite_sent. Route
 * every new "is this person activated" indicator through here instead.
 *
 * `expired` / `revoked` are additive states used by ContactProfilePage's own
 * richer portal-access management widget (which also tracks invite send/
 * expiry timestamps and exposes resend/revoke actions) — that widget is a
 * single-subject action panel, not a "label next to a name in a list", so it
 * keeps its own local copy for sub-text/button-label but shares this type +
 * tone so the two concepts stay one vocabulary rather than two.
 */
import type { PillTone } from '@/components/atoms/Pill'

export type PortalStatus = 'active' | 'invited' | 'not_invited' | 'expired' | 'revoked'

// Legacy wire values still emitted by GET /contacts/{id}/profile's top-level
// portal_status field (contact_profile_handlers.go's 5-state machine predates
// this unification). Folded into the canonical vocabulary at the boundary so
// nothing downstream has to know both spellings exist.
const LEGACY_ALIASES: Record<string, PortalStatus> = {
  none: 'not_invited',
  pending: 'invited',
}

/** Normalizes any raw wire value into the canonical vocabulary. Returns null for 'n/a' / empty (no portal concept applies — render nothing). */
export function normalizePortalStatus(raw?: string | null): PortalStatus | null {
  if (!raw || raw === 'n/a') return null
  return (LEGACY_ALIASES[raw] ?? raw) as PortalStatus
}

// A pill on an activated person is noise — active renders nothing.
export const PORTAL_STATUS_LABEL: Record<PortalStatus, string | null> = {
  active: null,
  invited: 'Invited',
  not_invited: 'Not activated',
  expired: 'Access expired',
  revoked: 'Access revoked',
}

export const PORTAL_STATUS_TONE: Record<PortalStatus, PillTone> = {
  active: 'active',
  invited: 'warning',
  not_invited: 'neutral',
  expired: 'blocked',
  revoked: 'blocked',
}

/** Derives status for an AssignablePerson (assignee pickers) — no wire portal_status field there, just is_user / invite_pending. */
export function portalStatusFromAssignablePerson(p: { is_user?: boolean; invite_pending?: boolean }): PortalStatus {
  if (!p.is_user) return 'not_invited'
  if (p.invite_pending) return 'invited'
  return 'active'
}

/** Derives status for a GET /team row (SettingsTeamPage) — keyed off invite_pending / invite_sent instead of a portal_status field. */
export function portalStatusFromInviteFlags(m: { invite_pending?: boolean; invite_sent?: boolean }): PortalStatus {
  if (m.invite_pending) return 'invited'
  if (!m.invite_sent) return 'not_invited'
  return 'active'
}
